import React, { Fragment, useRef, useMemo } from 'react';
import { ChevronDown as ArrowSVG } from '@td-client-frontend/icons';
import { Popover } from '@headlessui/react';
import clsx from 'clsx';
import { Button } from './form-controls/Button';

interface PaginationProps {
  onPageChange: (pageNumber: number) => void;
  selectedPage: number;
  itemsPerPage: ItemsPerPageOption;
  totalPages?: number;
  isLoading?: boolean;
  onItemsPerPageChange?: (selected: ItemsPerPageOption) => void;
}
export interface ItemsPerPageOption {
  label: string;
  value: number;
}

const PAGINATION_BLOCK_LENGTH = 3;

const options: ItemsPerPageOption[] = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 }
];

const Pagination = ({
  onItemsPerPageChange = () => null,
  onPageChange,
  totalPages = 1,
  selectedPage = 1,
  itemsPerPage = options[0],
  isLoading = false
}: PaginationProps) => {
  const popoverButtonRef = useRef<HTMLButtonElement>(null);

  const isFirstPage = selectedPage === 1;
  const hasNextPage = selectedPage !== totalPages;

  const handleItemsPerPageChange = (option: ItemsPerPageOption) => () => {
    onPageChange(1);
    onItemsPerPageChange(option);
    // used for closing headlessui popover
    popoverButtonRef.current?.click();
  };

  const handlePageChange = (page: number) => () => {
    onPageChange(page);
    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const pagesToDisplay = useMemo(() => {
    if (!isLoading) {
      const pageSize = PAGINATION_BLOCK_LENGTH * 2 + 1;

      let from = Math.max(1, selectedPage - PAGINATION_BLOCK_LENGTH);
      let to = Math.min(totalPages, selectedPage + PAGINATION_BLOCK_LENGTH);
      const pagesToDisplay = Array.from(new Array(to - from + 1), (x, i) => i + from);

      if (pagesToDisplay.length < pageSize && pagesToDisplay.length < totalPages) {
        const method = pagesToDisplay[0] === 1 ? 'push' : 'unshift';
        let diffCount = pageSize - pagesToDisplay.length;

        while (diffCount-- > 0 && pagesToDisplay.length !== totalPages) {
          pagesToDisplay[method](method === 'push' ? ++to : --from);
        }
      }
      return pagesToDisplay;
    }
    return [];
  }, [selectedPage, totalPages, isLoading]);

  return (
    <div className="flex items-center justify-between mb-10 py-6" data-testid="pagination-wrapper">
      {pagesToDisplay.length && (
        <>
          <div className="flex flex-row">
            <p className="mr-2">Items per page:</p>
            <Fragment>
              <Popover className="flex flex-wrap w-12 relative">
                {({ open }) => (
                  <Fragment>
                    <Popover.Button
                      ref={popoverButtonRef}
                      data-testid="items-per-page-button"
                      className="flex flex-row w-full justify-items-center items-center leading-tight text-gray-700 border rounded-lg shadow appearance-none border-gray-default focus:outline-none focus:bg-white focus:border-gray-500"
                    >
                      <p className="hidden p-1 ml-1 sm:block" data-testid="items-per-page-selected-value">
                        {itemsPerPage.label}
                      </p>
                      <ArrowSVG
                        aria-hidden
                        className={clsx(
                          'transition duration-200 transform',
                          open && 'rotate-180',
                          'block h-3 w-3 fill-current mx-1'
                        )}
                      />
                    </Popover.Button>
                    <Popover.Panel
                      className={clsx(
                        open ? 'absolute' : 'hidden',
                        'top-6 flex w-full text-base z-40 my-2 list-none text-center rounded shadow-lg justify-center flex-col bg-white'
                      )}
                    >
                      {options.map((option) => (
                        <li
                          onClick={handleItemsPerPageChange(option)}
                          onKeyDown={handleItemsPerPageChange(option)}
                          key={option.value}
                          role="option"
                          aria-selected={false}
                          className="hover:bg-gold cursor-pointer hover:border-primary-500 hover:shadow-dd-item-active"
                          data-testid={`items-per-page-li-${option.value}`}
                        >
                          {option.label}
                        </li>
                      ))}
                    </Popover.Panel>
                  </Fragment>
                )}
              </Popover>
            </Fragment>
          </div>
          <span data-testid="pag-page-status">
            Page {selectedPage} of {totalPages}
          </span>
          <div className="flex align-middle justify-center" data-testid="pag-navigation-wrapper">
            <Button
              kind="link"
              onClick={handlePageChange(1)}
              className={clsx(isFirstPage ? 'hidden' : 'block', 'text-blue-500 ml-2 h-auto')}
              data-testid="go-first-btn"
              name="first page"
            >
              First
            </Button>
            <Button
              kind="link"
              onClick={handlePageChange(selectedPage - 1)}
              className={clsx(isFirstPage ? 'hidden' : 'block', 'text-blue-500 ml-2 h-auto')}
              data-testid="go-previous-btn"
              name="previous page"
            >
              <ArrowSVG
                transform="rotate(90)"
                className="ml-2 cursor-pointer inline fill-current text-blue-500"
                aria-hidden
              />
            </Button>
            {pagesToDisplay.map((page) => (
              <Button
                onClick={handlePageChange(page)}
                key={page}
                kind="link"
                name={`page ${page}`}
                className={clsx(
                  'w-8 text-center inline h-auto ',
                  page === selectedPage ? 'bg-blue-500 rounded text-white' : 'text-blue-500'
                )}
              >
                {page}
              </Button>
            ))}
            <Button
              kind="link"
              onClick={handlePageChange(selectedPage + 1)}
              className={clsx(!hasNextPage ? 'hidden' : 'block', 'text-blue-500 ml-2 h-auto')}
              data-testid="go-next-btn"
              name="next page"
            >
              <ArrowSVG
                transform="rotate(270)"
                className="mr-2 cursor-pointer inline fill-current text-blue-500"
                aria-hidden
              />
            </Button>
            <Button
              kind="link"
              onClick={handlePageChange(totalPages)}
              className={clsx(!hasNextPage ? 'hidden' : 'block', 'text-blue-500 ml-2 h-auto')}
              data-testid="go-last-btn"
              name="last page"
            >
              Last
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Pagination;
